import type {
  ApplyTimelineItemUpdateParams,
  ApplyTimelineItemRemovalParams,
  ApplyMessageTimelineItemUpdateParams,
  ApplyCallTimelineItemUpdateParams,
  ApplyStatusHistoryTimelineItemUpdateParams,
} from '../../../types/cache/timeline-items/update.types';
import { rememberTimelineEvent } from './recent-events.util';

import {
  CallDocument,
  type Call_V2,
  FunnelStatusHistoryTC,
  MessageDocument,
  type Message,
  TCFunnelStatusChangedBySource,
  TCFunnelStatusHistoryDocument,
  type TCSearcherTimelineItemDocument as SearcherTimelineItemDocument,
} from 'src/api/generated';
import type { FunnelStatusHistoryWithContact } from '../../../../realtime/communications/types/status-history.types';
import { getMessageClientMessageId } from 'src/modules/shared/messaging/utils/client-message-id.util';
import { shouldAddTimelineItemToQuery } from 'src/modules/shared/communications/utils/cache/query-matcher.util';
import { sortByCreatedAtDesc } from 'src/modules/shared/utils/sort/created-at-sort.util';
import { type InfiniteQueryData } from 'src/modules/shared/utils/cache/update.util';

const convertMessageToTimelineItem = (
  message: Message,
  funnelId?: number
): SearcherTimelineItemDocument => ({
  ...message,
  funnelId: funnelId?.toString(),
  funnelConversationId: message.funnelConversationId?.toString(),
  sessionId: message.sessionId?.toString(),
  contactId: message.contactId?.toString(),
  modelType: MessageDocument.modelType.MESSAGE,
});

const convertCallToTimelineItem = (
  call: Call_V2,
  funnelId?: number
): SearcherTimelineItemDocument => ({
  ...call,
  id: call.id.toString(),
  userId: call.userId?.toString(),
  teamId: call.teamId?.toString(),
  contactId: call.contactId.toString(),
  sessionId: call.sessionId?.toString(),
  funnelId: funnelId?.toString(),
  funnelConversationId: call.funnelConversationId?.toString(),
  modelType: CallDocument.modelType.CALL,
});

const getStatusHistoryActorType = (
  changedBySource: FunnelStatusHistoryWithContact['changedBySource']
): TCFunnelStatusChangedBySource => {
  if (changedBySource === FunnelStatusHistoryTC.changedBySource.AI) {
    return TCFunnelStatusChangedBySource.AI;
  }
  if (changedBySource === FunnelStatusHistoryTC.changedBySource.SYSTEM) {
    return TCFunnelStatusChangedBySource.SYSTEM;
  }

  return TCFunnelStatusChangedBySource.USER;
};

const convertStatusHistoryToTimelineItem = (
  statusHistory: FunnelStatusHistoryWithContact,
  funnelId: number
): SearcherTimelineItemDocument => ({
  id: statusHistory.id.toString(),
  tenantId: statusHistory.tenantId,
  contactId: statusHistory.contactId?.toString(),
  funnelId: funnelId.toString(),
  funnelConversationId: statusHistory.funnelConversationId.toString(),
  fromStatusId: statusHistory.fromStatusId ?? undefined,
  toStatusId: statusHistory.toStatusId ?? undefined,
  changedAt: statusHistory.changedAt,
  createdAt: statusHistory.changedAt,
  updatedAt: statusHistory.changedAt,
  modelType: TCFunnelStatusHistoryDocument.modelType.STATUS_HISTORY,
  actor: {
    type: getStatusHistoryActorType(statusHistory.changedBySource),
    userId: statusHistory.changedById,
  },
});

const sortTimelineItemPages = (
  data: InfiniteQueryData<SearcherTimelineItemDocument>
): InfiniteQueryData<SearcherTimelineItemDocument> => {
  const pageSizes = data.pages.map((page) => page.items.length);
  const sortedItems = sortByCreatedAtDesc(
    data.pages.flatMap((page) => page.items)
  );
  let offset = 0;

  const pages = data.pages.map((page, index) => {
    const pageSize = pageSizes[index];
    const items = sortedItems.slice(offset, offset + pageSize);
    offset += pageSize;

    return {
      ...page,
      items,
    };
  });

  return {
    ...data,
    pages,
  };
};

/**
 * Applies a timeline document snapshot to matching timeline caches.
 *
 * @param params - Cache update inputs including the query client and timeline item.
 */
export const applyTimelineItemUpdate = ({
  queryClient,
  timelineItem,
  action,
}: ApplyTimelineItemUpdateParams): void => {
  rememberTimelineEvent(queryClient, {
    tenantId: timelineItem.tenantId,
    id: timelineItem.id,
    modelType: timelineItem.modelType,
    item: action === 'deleted' ? undefined : timelineItem,
    occurredAt: timelineItem.updatedAt,
  });
  const queries = queryClient.getQueriesData<
    InfiniteQueryData<SearcherTimelineItemDocument>
  >({ queryKey: ['crm/timeline-items/search'] });
  for (const [queryKey, data] of queries) {
    if (!data) continue;
    const incomingClientId =
      timelineItem.modelType === MessageDocument.modelType.MESSAGE
        ? getMessageClientMessageId(timelineItem)
        : undefined;
    const pages = data.pages.map((page) => ({
      ...page,
      items: page.items.flatMap((oldItem) => {
        if (oldItem.modelType !== timelineItem.modelType) return [oldItem];
        if (oldItem.id === timelineItem.id) {
          if (
            Date.parse(oldItem.updatedAt) > Date.parse(timelineItem.updatedAt)
          ) {
            return [oldItem];
          }
          return action === 'deleted' ? [] : [timelineItem];
        }
        if (
          incomingClientId &&
          oldItem.modelType === MessageDocument.modelType.MESSAGE &&
          oldItem.id.startsWith('temp-') &&
          (oldItem.id === incomingClientId ||
            getMessageClientMessageId(oldItem) === incomingClientId)
        ) {
          return [];
        }
        return [oldItem];
      }),
    }));
    if (
      !pages.some((page) =>
        page.items.some(
          (item) =>
            item.id === timelineItem.id &&
            item.modelType === timelineItem.modelType
        )
      ) &&
      action !== 'deleted' &&
      pages.length > 0 &&
      shouldAddTimelineItemToQuery(queryKey, timelineItem)
    ) {
      pages[0].items.unshift(timelineItem);
    }
    queryClient.setQueryData(
      queryKey,
      sortTimelineItemPages({ ...data, pages })
    );
  }
};

/**
 * Removes a timeline item by resource id when a delete event has no snapshot.
 *
 * @param params - Cache removal inputs including the resource id.
 */
export const applyTimelineItemRemoval = ({
  queryClient,
  resourceId,
  resourceType,
  occurredAt,
  tenantId,
}: ApplyTimelineItemRemovalParams): void => {
  rememberTimelineEvent(queryClient, {
    tenantId,
    id: resourceId,
    modelType: resourceType,
    occurredAt,
  });
  queryClient.setQueriesData<InfiniteQueryData<SearcherTimelineItemDocument>>(
    { queryKey: ['crm/timeline-items/search'] },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.filter(
            (item) => item.id !== resourceId || item.modelType !== resourceType
          ),
        })),
      }
  );
};

/**
 * Applies a realtime message event to timeline caches.
 *
 * @param params - Message timeline update inputs.
 */
export const applyMessageTimelineItemUpdate = ({
  queryClient,
  message,
  action,
  funnelId,
}: ApplyMessageTimelineItemUpdateParams): void => {
  applyTimelineItemUpdate({
    queryClient,
    timelineItem: convertMessageToTimelineItem(message, funnelId),
    action,
  });
};

/**
 * Applies a realtime call event to timeline caches.
 *
 * @param params - Call timeline update inputs.
 */
export const applyCallTimelineItemUpdate = ({
  queryClient,
  call,
  action,
  funnelId,
}: ApplyCallTimelineItemUpdateParams): void => {
  applyTimelineItemUpdate({
    queryClient,
    timelineItem: convertCallToTimelineItem(call, funnelId),
    action,
  });
};

/**
 * Applies a realtime status-history event to timeline caches.
 *
 * @param params - Status-history timeline update inputs.
 */
export const applyStatusHistoryTimelineItemUpdate = ({
  queryClient,
  statusHistory,
  funnelId,
}: ApplyStatusHistoryTimelineItemUpdateParams): void => {
  applyTimelineItemUpdate({
    queryClient,
    timelineItem: convertStatusHistoryToTimelineItem(statusHistory, funnelId),
    action: 'created',
  });
};
